import { useContext, useState } from "react"
import { EventSessionForm } from "@components"
import { NotificationContext, UserContext } from "@context"
import { getDateRangeStatus, getGraphQLErrorMessage } from "@helpers"
import { useCreateSession } from "@hooks"
import { useApolloClient } from "@apollo/client";
import { Button } from "@heroui/react"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import SessionErrorPlaceholder from "../SessionErrorPlaceholder"

const CreateSession = ({ handleCancel, event }) => {
  const [dirtyFields, setDirtyFields] = useState({})
  const [errors, setErrors] = useState({})
  const [isDirty, setIsDirty] = useState(false)

  /**
   * Context
   */
  const { notify } = useContext(NotificationContext)

  /**
   * Hooks
   */
  const client = useApolloClient();
  const { setCurrentEvent } = useContext(UserContext)
  const { createSession, loading: saving } = useCreateSession()

  /**
   * Functions
   */
  const handleDirtyChange = (isDirty, dirtyFields) => {
    setIsDirty(isDirty)
    setDirtyFields(dirtyFields)
  }
  
  const handleErrorChange = (errors) => setErrors(errors)
  
  const handleSave = (data) => {
    createSession({
      variables: {
        input: {
          event_id: event.id,
          starts_at: dayjs(data.start_date).format("YYYY-MM-DD") + " " + data.start_time.format("HH:mm:ss"),
          ends_at: dayjs(data.start_date).format("YYYY-MM-DD") + " " + data.end_time.format("HH:mm:ss"),
          slot_duration: parseInt(data.slot_duration, 10),
          slot_gap: parseInt(data.slot_gap, 10),
          title: data.title,
        },
      },
      
      onCompleted: (response) => {
        setCurrentEvent(event.id) // forces user context to refresh event data
        notify({ title: "Success", type: "success", content: `Session created successfully.`, open: true })
        client.refetchQueries({ include: "active" }); // refetch all active queries to reflect the new session
        handleCancel()
      },

      onError: (error) => {
        notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(error, `An error occurred while creating the session.`), open: true })
      }
    })
  }

  const hasEmptyFields = () => {
    return !dirtyFieldKeys.includes('title') || 
      !dirtyFieldKeys.includes('start_date') || 
      !dirtyFieldKeys.includes('start_time') || 
      !dirtyFieldKeys.includes('end_time')
  }

  const hasErrors = () => Object.keys(errors).length > 0

  /**
   * Render
   */
  const eventStatus = getDateRangeStatus(event.start_date, event.end_date, "milliseconds")
  const isEventLiveOrExpired = eventStatus === "Current" || eventStatus === "Expired"
  const dirtyFieldKeys = isDirty ? Object.keys(dirtyFields) : []
  const isSaveButtonDisabled = hasEmptyFields() || hasErrors()

  if (isEventLiveOrExpired) {
    return (
      <SessionErrorPlaceholder
        title="Session Creation Disabled"
        description="You cannot create a session because the event is either live or has expired."
        handleCancel={handleCancel}
      />
    )
  }

  return (      
    <EventSessionForm
      handleSubmit={(data) => handleSave(data)}
      session={undefined}
      onDirtyChange={(isDirty, dirtyFields) => handleDirtyChange(isDirty, dirtyFields)}
      onErrorChange={(errors) => handleErrorChange(errors)}
      isReadOnly={false}
    >
      <div className="flex flex-col gap-2 mt-6">
        <Button color="default" fullWidth onPress={handleCancel}>Cancel</Button>
        <Button className="font-semibold" type="submit" color="secondary" fullWidth isDisabled={isSaveButtonDisabled} isLoading={saving}>Save</Button>
      </div>
    </EventSessionForm>
  )
}

CreateSession.propTypes = {
  event: PropTypes.object.isRequired,
  handleCancel: PropTypes.func.isRequired,
}

export default CreateSession